import React, { useState, useEffect, useCallback } from "react";
import { X, Check, AlertTriangle, Loader2, RotateCw } from "lucide-react";

/**
 * Calculates straight-line distance in meters between two lat/lng coordinates (Haversine formula)
 */
export const calculateDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const toRad = (val) => (val * Math.PI) / 180;
  const R = 6371000; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

const GeofenceBottomSheetModal = ({
  isOpen,
  onClose,
  onProceed,
  destinationLat,
  destinationLng,
  isPickup = false,
  thresholdMeters = 100,
}) => {
  const [distanceMeters, setDistanceMeters] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [isLocating, setIsLocating] = useState(true);
  const [gpsError, setGpsError] = useState("");

  const verifyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError("Geolocation is not supported by your browser");
      setIsLocating(false);
      return;
    }

    setIsLocating(true);
    setGpsError("");

    const geoOptions = {
      enableHighAccuracy: true, // Forces satellite chip on physical phone
      timeout: 10000,
      maximumAge: 0,            // Disallow stale cached coordinates
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const uLat = position.coords.latitude;
        const uLng = position.coords.longitude;
        const acc = Math.round(position.coords.accuracy);

        setAccuracy(acc);

        const dLat = parseFloat(destinationLat);
        const dLng = parseFloat(destinationLng);

        if (!dLat || !dLng) {
          setDistanceMeters(0);
        } else {
          const meters = calculateDistanceInMeters(uLat, uLng, dLat, dLng);
          setDistanceMeters(meters);
        }

        setIsLocating(false);
      },
      (error) => {
        console.warn("GPS verification error:", error.message);
        if (error.code === error.PERMISSION_DENIED) {
          setGpsError("Location permission denied. Please allow location access in your browser settings.");
        } else if (error.code === error.TIMEOUT) {
          setGpsError("GPS lock timed out. Tap retry or verify manually.");
        } else {
          setGpsError("Position unavailable. Tap retry or verify manually.");
        }
        setIsLocating(false);
      },
      geoOptions
    );
  }, [destinationLat, destinationLng]);

  useEffect(() => {
    if (isOpen) {
      verifyLocation();
    }
  }, [isOpen, verifyLocation]);

  if (!isOpen) return null;

  const isWithinGeofence = distanceMeters !== null && distanceMeters <= thresholdMeters;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs animate-fadeIn">
      <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl border border-gray-200 animate-slideUp relative space-y-4">
        {/* Top-Right Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>

        {isLocating ? (
          /* ================= 1. VERIFYING GPS LOADING STATE ================= */
          <div className="py-8 flex flex-col items-center justify-center space-y-3 text-center">
            <Loader2 className="w-9 h-9 text-red-600 animate-spin" />
            <div className="space-y-1">
              <h3
                className="text-lg font-black text-gray-900"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                Verifying your location...
              </h3>
              <p
                className="text-xs text-gray-400"
                style={{ fontFamily: "DMSans-Regular, sans-serif" }}
              >
                Checking GPS distance to destination
              </p>
            </div>
          </div>
        ) : isWithinGeofence ? (
          /* ================= 2. WITHIN GEOFENCE (<= 100m) ================= */
          <div className="space-y-4 text-center pt-2">
            <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto shadow-md">
              <Check className="w-6 h-6 stroke-[3]" />
            </div>

            <div className="space-y-1">
              <h3
                className="text-xl font-black text-gray-900 leading-tight"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                {isPickup ? "Pickup location verified" : "Drop-off location verified"}
              </h3>
              <p
                className="text-xs text-gray-500"
                style={{ fontFamily: "DMSans-Regular, sans-serif" }}
              >
                You're within {distanceMeters} meters of the expected area.
                {accuracy && (
                  <span className="block text-[10px] text-gray-400 font-mono mt-0.5">
                    (GPS Accuracy: ±{accuracy}m)
                  </span>
                )}
              </p>
            </div>

            <button
              type="button"
              onClick={onProceed}
              className="w-full h-14 bg-[#E9001E] hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md flex items-center justify-center cursor-pointer"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              {isPickup ? "Collect Items" : "Continue to Delivery Photo"}
            </button>
          </div>
        ) : (
          /* ================= 3. OUTSIDE GEOFENCE (> 100m) OR GPS ERROR ================= */
          <div className="space-y-4 text-center pt-2">
            <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center mx-auto shadow-md">
              <AlertTriangle className="w-6 h-6 stroke-[2.5]" />
            </div>

            <div className="space-y-1.5">
              <h3
                className="text-xl font-black text-gray-900 leading-tight"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                {isPickup ? "You're outside the pickup area" : "You're outside the drop-off area"}
              </h3>
              <p
                className="text-xs text-gray-500 leading-snug"
                style={{ fontFamily: "DMSans-Regular, sans-serif" }}
              >
                {distanceMeters !== null ? (
                  <>
                    Your location appears to be{" "}
                    <span className="font-bold text-gray-800 font-mono">
                      {distanceMeters >= 1000
                        ? `${(distanceMeters / 1000).toFixed(1)} km`
                        : `${distanceMeters} m`}
                    </span>{" "}
                    from the expected area.
                  </>
                ) : (
                  <span>{gpsError || "Could not retrieve live GPS coordinates."}</span>
                )}
              </p>
            </div>

            <div className="pt-1">
              <h4
                className="text-sm font-black text-gray-900"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                {isPickup
                  ? "Are you physically at the correct pickup location?"
                  : "Are you physically at the correct drop-off location?"}
              </h4>
            </div>

            {/* Action Buttons: Matching H-14 rounded-2xl CTA styles */}
            <div className="space-y-2.5 pt-2">
              <button
                type="button"
                onClick={onProceed}
                className="w-full h-14 bg-[#E9001E] hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md flex items-center justify-center cursor-pointer"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                Yes, I'm at the Location
              </button>

              <button
                type="button"
                onClick={verifyLocation}
                className="w-full h-14 bg-white hover:bg-gray-50 border border-gray-300 active:scale-[0.98] text-gray-800 font-black text-sm uppercase tracking-wider rounded-2xl transition flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                <RotateCw className="w-4 h-4 text-gray-500" />
                <span>Retry Location</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeofenceBottomSheetModal;